import {
  Button,
  Flex,
  Grid,
  Link,
  Text,
  View,
  ActionButton,
  MenuTrigger,
  Menu,
  Item
} from '@adobe/react-spectrum';
import moment from 'moment';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { RoadmapCard } from '.';
import { KeyDateTitleWithTooltip } from './KeyDateTitleWithTooltip';

import { calculateColSpan } from 'utils/helpers';
import { selectCompact } from 'app/pages/Home/slice/selectors';
import { useSelector } from 'react-redux';
import TimelineCard from '../TimelineCard/TimelineCard';
import '../TimelineCard/ContextMenu.module.scss';
import styles from './Row.module.scss';
import { RootState } from 'types';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useDispatch } from 'react-redux';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import S2IconMore from '../../assets/icons/S2_Icon_More_20_N.svg';
import { EditEventModal } from '../RsdForms/EditEventModal';
import { updateMoment } from '../RsdForms/api';
import { STATUS_VALUES, MOMENT_TYPES } from 'utils/constants';
import { eolTimelineColumnPlacement } from './eolTimelineColumnPlacement';
import { EnhancedAlertBanner } from '../RsdForms/EnhancedAlertBanner';
import { useEventKeyDatePermissions } from '../../utils/rbacHandler';
import {
  deleteMoment,
  clearEventsCache,
  clearParentMomentsCache
} from '../../services/momentsApi';
import { ToastQueue } from '@adobe/react-spectrum';

// Shared stable reference so TimelineCard's React.memo isn't defeated by a
// fresh `[]` literal being passed as childRelationships/features/relationships
// on every render when a card has none. Never mutated by any consumer.
const EMPTY_ARRAY: any[] = [];

interface RowProps {
  release: any;
  quarters: any;
  colSpan?: number;
  expandedQuarters: string[];
  isKeyDate?: boolean;
  gapWidth?: number;
  customLabel?: string; // Custom label for Must Wins view (audience-strategy name)
  isMustWinView?: boolean; // Flag to indicate Must Wins view for sticky positioning
  /** In Must Wins view, unique key for this row (audienceIdx-strategyIdx). Used to build per-instance expand keys so the same card in different rows expands independently. */
  mustWinRowKey?: string;
}

const RowComponent = ({
  release,
  quarters,
  expandedQuarters,
  isKeyDate,
  gapWidth,
  customLabel,
  isMustWinView,
  mustWinRowKey
}: RowProps) => {
  const isCompact = useSelector(selectCompact);
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const { isAdmin, canEditKeyDates, canDeleteKeyDates } =
    useEventKeyDatePermissions();

  // Three dots menu functionality for Key Dates (admin only)

  // Edit modal state for Key Dates
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedKeyDate, setSelectedKeyDate] = useState<any>(null);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [openMenuCardId, setOpenMenuCardId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Notification state for mark as complete actions
  const [notification, setNotification] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
  } | null>(null);

  // Handler for Key Date menu actions
  const handleKeyDateMenuAction = async (action: any, card: any) => {
    const actionStr = String(action);

    // Only allow actions for admin users
    if (!isAdmin) {
      return;
    }

    switch (actionStr) {
      case 'edit':
        setSelectedKeyDate(card);
        setIsEditModalOpen(true);
        break;
      case 'delete':
        setSelectedKeyDate(card);
        setShowDeleteConfirmation(true);
        break;
      case 'markComplete':
        await handleMarkAsComplete(card);
        break;
      default:
        break;
    }
  };

  // Handler for marking key dates/events as complete
  const handleMarkAsComplete = async (card: any) => {
    try {
      // Update status to completed
      const updatePayload = {
        status: STATUS_VALUES.COMPLETE
      };

      await updateMoment(card.issueId, updatePayload);

      // Refresh the data to show updated status
      dispatch(actions.loadReleaseData({}));

      // Show success notification
      setNotification({
        message: `${
          isKeyDate ? 'Key Dates' : 'Events'
        } marked as complete successfully!`,
        variant: 'success'
      });
    } catch (error: any) {
      console.error('❌ Error marking as complete:', error);

      // Show error notification
      setNotification({
        message:
          error?.message ||
          `Failed to mark ${
            isKeyDate ? 'key dates' : 'events'
          } as complete. Please try again.`,
        variant: 'error'
      });
    }
  };

  // Handler for closing edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedKeyDate(null);
  };

  // Handler for successful edit - refresh data seamlessly
  const handleEditSuccess = () => {
    // Refresh the moments data to get updated information
    dispatch(actions.loadReleaseData({}));
  };

  // Handler for delete confirmation
  const handleConfirmDelete = async () => {
    if (!selectedKeyDate?.issueId) {
      ToastQueue.negative('Unable to delete: Missing item ID', {
        timeout: 5000
      });
      setShowDeleteConfirmation(false);
      return;
    }

    setShowDeleteConfirmation(false);

    try {
      // Backend saveDeleteAuditHistory on DELETE /moments/{id}; no UI /audits POST.
      const result = await deleteMoment(selectedKeyDate.issueId);

      if (result.success && result.status === 204) {
        // Show success toast
        ToastQueue.positive('Key Date has been deleted', { timeout: 5000 });

        // Clear events/parent moments cache to refresh the lists
        clearEventsCache();
        clearParentMomentsCache();

        // Refresh the roadmap data
        dispatch(actions.loadReleaseData({}));

        // Clear selected key date
        setSelectedKeyDate(null);
      } else {
        // Show error toast
        ToastQueue.negative('Unable to delete Key Date', { timeout: 5000 });
        console.error('Delete failed:', result.error || result.message);
      }
    } catch (error) {
      // Show error toast
      ToastQueue.negative('Unable to delete Key Date', { timeout: 5000 });
      console.error('Error deleting Key Date:', error);
    }
  };

  // Handler for canceling deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSelectedKeyDate(null);
  };

  // Helper function to render KeyDate menu
  const renderKeyDateMenu = (card: any) => (
    <MenuTrigger
      align="end"
      direction="bottom"
      shouldFlip={true}
      onOpenChange={isOpen => setOpenMenuCardId(isOpen ? card.issueId : null)}
    >
      <ActionButton
        aria-label="More actions"
        isQuiet
        UNSAFE_style={{
          display: 'flex',
          width: '20px',
          height: '20px',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '5.5px',
          background: 'transparent',
          border: 'none',
          padding: '2px',
          minWidth: '20px',
          minHeight: '20px'
        }}
      >
        <img
          src={S2IconMore}
          alt="More options"
          style={{
            width: '16px',
            height: '16px',
            display: 'block'
          }}
        />
      </ActionButton>
      <Menu
        onAction={action => handleKeyDateMenuAction(action, card)}
        UNSAFE_style={{
          zIndex: 3,
          background: '#FFFFFF',
          border: 'none',
          borderRadius: '6px',
          boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.2)',
          padding: '10px',
          minWidth: '140px',
          maxWidth: '200px'
        }}
        UNSAFE_className="custom-context-menu"
      >
        <Item key="edit">Edit</Item>
        <Item key="delete">Delete</Item>
        <Item key="markComplete">Mark as Complete</Item>
      </Menu>
    </MenuTrigger>
  );

  const formatDateRange = dates => {
    if (dates.targetStart && dates.targetEnd) {
      const start = moment(dates.targetStart);
      const end = moment(dates.targetEnd);

      const startDay = start.date();
      const startMonth = start.format('MMM').toUpperCase();
      const endDay = end.date();
      const endMonth = end.format('MMM').toUpperCase();

      // Check if start and end dates are the same
      if (dates.targetStart === dates.targetEnd) {
        return `${startDay} ${startMonth}`;
      } else if (startMonth === endMonth) {
        return `${startDay}-${endDay} ${startMonth}`;
      } else {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
      }
    } else if (dates.targetStart) {
      const start = moment(dates.targetStart);
      return `${start.date()} ${start.format('MMM').toUpperCase()}`;
    } else if (dates.targetMonth) {
      // Handle targetMonth case
      const startMonth = dates.targetMonth.split(' ')[0].toUpperCase(); // Returns just the month part (e.g., "JUL")
      if (dates.targetEndMonth) {
        const endMonth = dates.targetEndMonth.split(' ')[0].toUpperCase();
        return `${startMonth} - ${endMonth}`; // Returns "JUL - AUG" format with spaces
      }
      return startMonth; // Returns just "JUL" if no end month
    } else if (dates.targetEnd) {
      const end = moment(dates.targetEnd);
      return `${end.date()} ${end.format('MMM').toUpperCase()}`;
    }
    return '';
  };
  const RenderWithNewLine = ({ type }) => {
    const parts = type.split('/');

    return (
      <div className={styles.text}>
        {parts.map((part, index) => (
          <Text key={index}>
            {part}
            {index < parts.length - 1 && <br />}{' '}
          </Text>
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Success/Error Notification Banner */}
      {notification && (
        <EnhancedAlertBanner
          variant={notification.variant}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoHide={true}
          autoHideDelay={4000}
        />
      )}
      {/* <Grid
        columns="repeat(15, 1fr)" // Total 13 columns: 1 for headings + 12 for quarters/months
        UNSAFE_style={{
          overflowY: 'scroll',
          width: '100%'
        }}
      > */}
      <Flex direction="row" UNSAFE_style={{ minWidth: 'max-content' }}>
        <div
          data-testid={`roadmap-row-header-${release.type
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
          className={styles.gridHeader}
        >
          <div
            className={styles.heading}
            data-testid={`roadmap-row-heading-${release.type
              .replace(/[^a-zA-Z0-9]/g, '-')
              .toLowerCase()}`}
          >
            <RenderWithNewLine type={customLabel || release.type} />
          </div>
        </div>

        <Flex
          data-testid={`roadmap-row-quarters-${release.type
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
        >
          {quarters.map((quarter, index) => {
            const isExpanded = expandedQuarters.includes(quarter.label);
            // Find the relevant data for the current quarter
            const quarterData = release.projects?.find(
              data => data.quarter === quarter.label
            );

            return (
              <View
                key={quarter.id}
                data-testid={`roadmap-row-quarter-${quarter.label
                  .replace(/\s+/g, '-')
                  .toLowerCase()}-${release.type
                  .replace(/[^a-zA-Z0-9]/g, '-')
                  .toLowerCase()}`}
                backgroundColor={isKeyDate ? 'gray-100' : 'gray-50'}
                width={
                  expandedQuarters.includes(quarter.label)
                    ? '65rem'
                    : '21.875rem' // Increased: expanded 61.25rem->65rem (3 cards × 20px = 60px increase), compact 21.25rem->21.875rem (1 card × 10px = 10px increase)
                }
                UNSAFE_className={styles.rowColumn}
                UNSAFE_style={{
                  borderBottom: isMustWinView
                    ? '1px solid #dadada'
                    : '1px solid #E6E6E6',
                  background: '#FBFBFB',
                  borderRight: '1px solid #E6E6E6',
                  paddingTop: !isKeyDate ? '8px' : '0px',
                  paddingBottom: !isKeyDate ? '8px' : '0px',
                  textAlign: isKeyDate ? 'left' : 'center',
                  alignItems: 'stretch'
                }}
              >
                <Grid
                  data-testid={`roadmap-row-grid-${quarter.label
                    .replace(/\s+/g, '-')
                    .toLowerCase()}-${release.type
                    .replace(/[^a-zA-Z0-9]/g, '-')
                    .toLowerCase()}`}
                  UNSAFE_className={
                    isKeyDate
                      ? 'key-date-container'
                      : isExpanded && !isCompact
                      ? styles.expandedContainer
                      : `compact-container`
                  }
                  columns={isExpanded && !isCompact ? `repeat(3, 1fr)` : `1fr`}

                  // justifyItems={!isKeyDate ? 'center' : 'start'}
                >
                  {isExpanded && !isCompact ? (
                    quarter.months.map(month => (
                      <Flex
                        gap="size-100"
                        key={month}
                        width={'100%'}
                        direction={'column'}
                        UNSAFE_className={
                          isKeyDate && month !== quarter.months[2]
                            ? 'key-date-borders'
                            : styles['card-container-expanded']
                        }
                      >
                        {(quarterData?.project || [])
                          .filter(card => {
                            let cardMonth = quarter.months[2]; // Default to last month of quarter

                            const eolPlacement = eolTimelineColumnPlacement(
                              card,
                              month,
                              quarter.months[2]
                            );
                            if (eolPlacement !== null) {
                              return eolPlacement;
                            }

                            // Use the same date logic as TimelineCard for consistency
                            // This matches getDisplayDates() in TimelineCard.tsx
                            const shouldUseSecondaryDates =
                              card?.showOnTimeline;

                            if (shouldUseSecondaryDates) {
                              // showOnTimeline = true: Use release/effective dates only
                              // Priority: releaseDate > effectiveDate > releaseMonth > effectiveMonth > releaseQuarter > effectiveQuarter
                              const secondaryDate =
                                card?.dates?.releaseDate ||
                                card?.dates?.effectiveDate;
                              if (secondaryDate) {
                                cardMonth = moment(secondaryDate).format('MMM');
                              } else if (card?.dates?.releaseMonth) {
                                cardMonth =
                                  card?.dates?.releaseMonth.split(' ')[0];
                              } else if (card?.dates?.effectiveMonth) {
                                cardMonth =
                                  card?.dates?.effectiveMonth.split(' ')[0];
                              }
                              // releaseQuarter/effectiveQuarter: use default (last month of quarter) since we can't determine specific month
                            } else {
                              // showOnTimeline = false: Use target dates primarily
                              // Only fallback to release/effective dates if NO target dates exist at all
                              const hasTargetDates =
                                card?.dates?.targetStart ||
                                card?.dates?.targetEnd ||
                                card?.dates?.targetMonth ||
                                card?.dates?.targetQuarter;

                              if (hasTargetDates) {
                                // Use target dates only - don't mix with release/effective dates
                                // Priority: targetStart > targetEnd > targetMonth > targetQuarter
                                if (card?.dates?.targetStart) {
                                  cardMonth = moment(
                                    card.dates.targetStart
                                  ).format('MMM');
                                } else if (card?.dates?.targetEnd) {
                                  cardMonth = moment(
                                    card.dates.targetEnd
                                  ).format('MMM');
                                } else if (card?.dates?.targetMonth) {
                                  cardMonth =
                                    card.dates.targetMonth.split(' ')[0];
                                }
                                // targetQuarter: use default (last month of quarter) since we can't determine specific month
                              } else {
                                // No target dates — for phased offer/program cards use
                                // customerNotificationDate/Month (the roadmap-facing date)
                                // before falling back to effectiveDate/Month.
                                const isPhasedOfferCard =
                                  card?.releasePhaseId &&
                                  (card?.projectType ===
                                    MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS ||
                                    card?.projectType ===
                                      MOMENT_TYPES.PROMOS_OFFERS);

                                if (isPhasedOfferCard) {
                                  const custNotifDate =
                                    card?.dates?.customerNotificationDate;
                                  const custNotifMonth =
                                    card?.dates?.customerNotificationMonth ||
                                    (card?.dates as any)
                                      ?.customerNotificationDateMonth;
                                  if (custNotifDate) {
                                    cardMonth =
                                      moment(custNotifDate).format('MMM');
                                  } else if (custNotifMonth) {
                                    cardMonth = custNotifMonth.split(' ')[0];
                                  }
                                } else {
                                  // Fallback to release/effective dates
                                  const fallbackDate =
                                    card?.dates?.releaseDate ||
                                    card?.dates?.effectiveDate;
                                  if (fallbackDate) {
                                    cardMonth =
                                      moment(fallbackDate).format('MMM');
                                  } else if (card?.dates?.releaseMonth) {
                                    cardMonth =
                                      card.dates.releaseMonth.split(' ')[0];
                                  } else if (card?.dates?.effectiveMonth) {
                                    cardMonth =
                                      card.dates.effectiveMonth.split(' ')[0];
                                  }
                                }
                              }
                            }

                            return month.startsWith(cardMonth);
                          })
                          .map(card => (
                            <React.Fragment key={card.id}>
                              {isKeyDate ? (
                                <div
                                  style={{
                                    position: 'relative',
                                    width: '100%'
                                  }}
                                  onMouseEnter={e => {
                                    e.stopPropagation();
                                    setHoveredCardId(card.issueId);
                                  }}
                                  onMouseLeave={e => {
                                    e.stopPropagation();
                                    setHoveredCardId(null);
                                  }}
                                >
                                  <Grid
                                    UNSAFE_className={styles.cardContainer}
                                    columns="90px 1fr"
                                    UNSAFE_style={{ gap: '4px' }}
                                    alignItems="center"
                                  >
                                    <Text UNSAFE_style={{ fontWeight: 'bold' }}>
                                      {(card.dates.targetStart ||
                                        card.dates.targetEnd ||
                                        card.dates.targetMonth) &&
                                        formatDateRange(card.dates)}
                                    </Text>
                                    <KeyDateTitleWithTooltip
                                      title={card.title}
                                    />
                                  </Grid>
                                  {/* Three dots menu for Key Dates - Only show for admins and ACTIVE status */}
                                  {isAdmin &&
                                    (isKeyDate ||
                                      card.projectType === 'Key Dates' ||
                                      card.projectType === 'Key Date') &&
                                    card.status === STATUS_VALUES.ACTIVE &&
                                    card.issueId &&
                                    card.title &&
                                    (hoveredCardId === card.issueId ||
                                      openMenuCardId === card.issueId) && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          right: '8px',
                                          transform: 'translateY(-50%)',
                                          zIndex: 2
                                        }}
                                      >
                                        {renderKeyDateMenu(card)}
                                      </div>
                                    )}
                                </div>
                              ) : (
                                <TimelineCard
                                  releasePhaseId={card.releasePhaseId}
                                  projectType={card.projectType}
                                  issueId={card.issueId}
                                  title={card.title}
                                  lastUpdated={card.lastUpdated}
                                  reportingSummary={card.reportingSummary}
                                  categories={card.categories}
                                  products={card.products}
                                  riskLevel={
                                    card.riskLevel as
                                      | 'Low'
                                      | 'Moderate'
                                      | 'High'
                                  }
                                  releasePhase={card.releasePhase}
                                  status={card.status}
                                  type={release.type}
                                  dates={card.dates}
                                  isCompact={isCompact}
                                  links={card.links}
                                  priority={card.priority}
                                  childRelationships={
                                    card.childRelationships || EMPTY_ARRAY
                                  }
                                  features={card.features || EMPTY_ARRAY}
                                  relationships={
                                    card.relationships || EMPTY_ARRAY
                                  }
                                  isQuarterExpanded={isExpanded}
                                  relationship={card.relationship}
                                  wholeCard={card}
                                  mustWinInstanceKey={
                                    mustWinRowKey
                                      ? `${mustWinRowKey}-${quarter.label}-${
                                          card.issueId
                                        }-${card.releasePhaseId || ''}-${
                                          card.showOnTimeline ?? ''
                                        }`
                                      : undefined
                                  }
                                />
                              )}
                            </React.Fragment>
                          ))}
                      </Flex>
                    ))
                  ) : (
                    <Flex
                      gap="size-100"
                      width={'100%'}
                      direction={'column'}
                      alignItems={isKeyDate ? 'start' : 'center'}
                      UNSAFE_className={styles.cardRowCompect}
                      UNSAFE_style={{
                        alignItems: 'center'
                      }}
                    >
                      {(quarterData?.project || []).map(card => (
                        <React.Fragment key={card.id}>
                          {isKeyDate ? (
                            <div
                              style={{
                                position: 'relative',
                                width: '100%'
                              }}
                              onMouseEnter={e => {
                                e.stopPropagation();
                                setHoveredCardId(card.issueId);
                              }}
                              onMouseLeave={e => {
                                e.stopPropagation();
                                setHoveredCardId(null);
                              }}
                            >
                              <Grid
                                columns="95px 1fr"
                                UNSAFE_style={{ gap: '4px' }}
                                alignItems="center"
                              >
                                <Text UNSAFE_style={{ fontWeight: 'bold' }}>
                                  {(card.dates.targetStart ||
                                    card.dates.targetEnd ||
                                    card.dates.targetMonth) &&
                                    formatDateRange(card.dates)}
                                </Text>
                                <KeyDateTitleWithTooltip title={card.title} />
                              </Grid>
                              {/* Three dots menu for Key Dates - Only show for admins and ACTIVE status */}
                              {isAdmin &&
                                (isKeyDate ||
                                  card.projectType === 'Key Dates' ||
                                  card.projectType === 'Key Date') &&
                                card.status === STATUS_VALUES.ACTIVE &&
                                card.issueId &&
                                card.title &&
                                (hoveredCardId === card.issueId ||
                                  openMenuCardId === card.issueId) && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      right: '8px',
                                      transform: 'translateY(-50%)',
                                      zIndex: 2
                                    }}
                                  >
                                    {renderKeyDateMenu(card)}
                                  </div>
                                )}
                            </div>
                          ) : (
                            <TimelineCard
                              releasePhaseId={card.releasePhaseId}
                              projectType={card.projectType}
                              issueId={card.issueId}
                              title={card.title}
                              lastUpdated={card.lastUpdated}
                              reportingSummary={card.reportingSummary}
                              categories={card.categories}
                              products={card.products}
                              riskLevel={
                                card.riskLevel as 'Low' | 'Moderate' | 'High'
                              }
                              releasePhase={card.releasePhase}
                              status={card.status}
                              type={release.type}
                              dates={card.dates}
                              priority={card.priority}
                              links={card.links}
                              isCompact={isCompact}
                              childRelationships={
                                card.childRelationships || EMPTY_ARRAY
                              }
                              features={card.features || EMPTY_ARRAY}
                              relationships={card.relationships || EMPTY_ARRAY}
                              isQuarterExpanded={isExpanded}
                              relationship={card.relationship}
                              releasePhases={card.releasePhases}
                              showOnTimeline={card.showOnTimeline}
                              wholeCard={card}
                              mustWinInstanceKey={
                                mustWinRowKey
                                  ? `${mustWinRowKey}-${quarter.label}-${
                                      card.issueId
                                    }-${card.releasePhaseId || ''}-${
                                      card.showOnTimeline ?? ''
                                    }`
                                  : undefined
                              }
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </Flex>
                  )}
                </Grid>
              </View>
            );
          })}

          <div
            className={styles.whiteGapFiller}
            style={{
              width: gapWidth ? `${gapWidth}px` : undefined
            }}
          ></div>
        </Flex>
      </Flex>
      {/* </Grid> */}

      {/* Edit Key Date Modal */}
      {isEditModalOpen && selectedKeyDate && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          itemType="Key Dates"
          issueId={selectedKeyDate.issueId}
          initialValues={{
            title: selectedKeyDate.title || '',
            eventName: selectedKeyDate.title || '',
            name: selectedKeyDate.title || '',
            projectType: selectedKeyDate.projectType || 'Key Dates',
            momentType: 'Key Dates',
            startDate: selectedKeyDate.dates?.targetStart || null,
            endDate: selectedKeyDate.dates?.targetEnd || null,
            // Pass the mapped scheduleRange and dateRange for month-based dates
            scheduleRange: selectedKeyDate.scheduleRange || null,
            dateRange: selectedKeyDate.dateRange || null,
            // Also pass the raw dates object for fallback processing
            dates: selectedKeyDate.dates || null,
            description: selectedKeyDate.reportingSummary || '',
            id: selectedKeyDate.issueId || '',
            categories: selectedKeyDate.categories || '',
            products: selectedKeyDate.products || '',
            labels: selectedKeyDate.labels || [],
            riskLevel: selectedKeyDate.riskLevel || '',
            status: selectedKeyDate.status || '',
            priority: selectedKeyDate.priority || ''
          }}
        />
      )}

      {/* Delete Confirmation Dialog - Using Portal to render at body level */}
      {showDeleteConfirmation &&
        selectedKeyDate &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}
            onClick={e => {
              // Close if clicking on backdrop
              if (e.target === e.currentTarget) {
                handleCancelDelete();
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <h2
                style={{
                  color: '#000',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '20px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  margin: '0 0 16px 0'
                }}
              >
                Delete Key Date?
              </h2>
              <p
                style={{
                  color: '#6d6d6d',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '14px',
                  lineHeight: 1.5,
                  margin: '0 0 24px 0'
                }}
              >
                Are you sure you want to delete this key date? This action
                cannot be undone.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}
              >
                <button
                  onClick={handleCancelDelete}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#e6e6e6',
                    color: '#222222',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#d7373f',
                    color: '#ffffff',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </React.Fragment>
  );
};

export const Row = React.memo(RowComponent);
Row.displayName = 'Row';
